/**
 * Озвучка без реплики оригинала («сирота»).
 *
 * 4119e839, ru_es, папка 1, 1:22, «Así es.», 02.10.2026: «при удалении отрывка на оригинале
 * озвучка на испанском осталась. Не могу найти как её удалить». Русская `{id}_ru.json` ушла в
 * delete/ пары, а испанские `{id}_es.json` и wav остались живыми. Строки редактора строятся по
 * репликам оригинала (пара — по id), поэтому у такой озвучки строки нет: она была только блоком в
 * колонке озвучки, звучала, а флажок рядом выбирал её лишь для общей озвучки и скорости — удалить
 * её было нечем.
 *
 * Теперь озвучка перевода, чьего id нет среди реплик оригинала той же папки, несёт пометку
 * `orphan_voicing`. Она молчит — по одному правилу `voicingSilenced` (dubSounding.ts): её не
 * играет расписание, она не ставит границ перетаскиванию, не толкает соседей и не закрывает паузы
 * озвучки. Сборка (translate-auto-generated) такие реплики тоже пропускает — редактор и видео
 * согласны. В колонке она нарисована красной пунктирной рамкой, объясняет себя словами и
 * удаляется 🗑: перевод и его звук в паре, затем папка перечитывается.
 */
import { formatCueTime } from "./editorErrors";
import { deleteAudioAction, deleteTranslatedSubtitleAction } from "./actions";
import { AudioFolderData, ILang, ISubtitlesDataAll, MetadataToSubtitlesLanguages } from "./types";

type SourceIds = Iterable<string> | null | undefined;

/**
 * id озвучек папки, у которых нет реплики оригинала в той же папке (сравнение по id записи —
 * так редактор и строит пары). Список оригинала не известен (null: папка не загружена) или пуст —
 * сирот нет: это не приговор переводу. Ни одного общего id — тоже нет: это два пространства имён
 * (старые импорты, данные другой лекции на миг загрузки), а не удаления, и глушить по такому
 * сравнению всю колонку нельзя.
 */
export function orphanVoicingIds(tracks: ReadonlyArray<{ id: string }>, sourceIds: SourceIds): Set<string> {
    const orphans = new Set<string>();
    if (!sourceIds) return orphans;
    const source = new Set(Array.from(sourceIds, String));
    if (!source.size) return orphans;
    const ids = tracks.map((track) => String(track.id));
    if (!ids.some((id) => source.has(id))) return orphans;
    ids.forEach((id) => {
        if (!source.has(id)) orphans.add(id);
    });
    return orphans;
}

/**
 * Пометить сирот папки (`orphan_voicing: true`) и снять пометку с тех, чья реплика оригинала
 * вернулась. Порядок и число записей те же; незатронутые записи — те же объекты, входные не
 * меняются.
 */
export function markOrphanVoicings<T extends { id: string; orphan_voicing?: boolean }>(
    tracks: T[],
    sourceIds: SourceIds,
): T[] {
    const orphans = orphanVoicingIds(tracks, sourceIds);
    return tracks.map((track) => {
        if (orphans.has(String(track.id))) {
            return track.orphan_voicing === true ? track : { ...track, orphan_voicing: true };
        }
        if (track.orphan_voicing === undefined) return track;
        const copy = { ...track };
        delete copy.orphan_voicing;
        return copy;
    });
}

/** id реплик оригинала папки (1-based) или null, если папка оригинала не загружена. */
export const sourceCueIdsOf = (
    data: Partial<ISubtitlesDataAll>,
    sourceLang: ILang,
    folder: number,
): string[] | null => {
    const rows = data[sourceLang]?.[folder - 1]?.translations;
    return Array.isArray(rows) ? rows.map((row) => String(row.id)) : null;
};

/**
 * Все папки перевода, каждая — против своей папки оригинала (папки пары лежат в одном порядке:
 * их приносит один ответ сервера). Нетронутая папка — тот же объект.
 */
export function markOrphanVoicingsInFolders(
    target: AudioFolderData[],
    source: AudioFolderData[] | undefined,
): AudioFolderData[] {
    if (!source) return target;
    return target.map((folderData, index) => {
        const rows = source[index]?.translations;
        const marked = markOrphanVoicings(
            folderData.translations || [],
            Array.isArray(rows) ? rows.map((row) => String(row.id)) : null,
        );
        return marked.every((track, i) => track === folderData.translations[i])
            ? folderData
            : { ...folderData, translations: marked };
    });
}

// --- Слова ---------------------------------------------------------------------------------
// Язык оригинала назван: у лекции, переведённой с английского, русской реплики нет.

const sourceWords = (code: string | undefined) => {
    const lang = String(code || "ru").toLowerCase();
    if (lang === "ru") return { nom: "русская", gen: "русской", en: "Russian" };
    if (lang === "en") return { nom: "английская", gen: "английской", en: "English" };
    return { nom: null, gen: null, en: lang.toUpperCase() };
};

const quoteText = (text: string | undefined, en: boolean): string => {
    const clean = (text || "").trim();
    if (!clean) return en ? "(no text)" : "(без текста)";
    return `«${clean.length > 300 ? `${clean.slice(0, 300)}…` : clean}»`;
};

/** Подсказка на блоке сироты: что это и что с ним делать. */
export const orphanVoicingHint = (sourceLang: string | undefined, en: boolean): string => {
    const words = sourceWords(sourceLang);
    if (en) {
        return `Voicing without a source cue: its ${words.en} cue was deleted. It must not be in the video — delete it.`;
    }
    const cue = words.nom ? `её ${words.nom} реплика` : "её реплика оригинала";
    return `Озвучка без реплики оригинала: ${cue} удалена. В видео её быть не должно — удалите её.`;
};

/** Подпись 🗑 на блоке сироты. */
export const orphanDeleteTitle = (en: boolean): string =>
    en ? "Delete the voicing without a source cue" : "Удалить озвучку без реплики";

export interface OrphanNaming {
    /** Время реплики в папке, мс — то, что написано на блоке. */
    startMs: number | null | undefined;
    /** Текст перевода озвучки. */
    text?: string;
    sourceLang?: string;
}

/** Вопрос перед удалением: какая озвучка (время и текст) и почему её можно удалить. */
export const orphanDeleteQuestion = ({ startMs, text, sourceLang }: OrphanNaming, en: boolean): string => {
    const words = sourceWords(sourceLang);
    const named = `${formatCueTime(startMs)} ${quoteText(text, en)}`;
    if (en) return `Delete the voicing ${named}? Its ${words.en} cue no longer exists.`;
    const gone = words.gen ? `Её ${words.gen} реплики` : "Её реплики оригинала";
    return `Удалить озвучку ${named}? ${gone} больше нет.`;
};

/** Отказ — в красной полосе: какая озвучка не удалена и почему (словами сервера). */
export const orphanDeleteRefusal = ({ startMs, text }: OrphanNaming, reason: string, en: boolean): string =>
    en
        ? `Voicing ${formatCueTime(startMs)} ${quoteText(text, en)} was not deleted: ${reason}`
        : `Озвучка ${formatCueTime(startMs)} ${quoteText(text, en)} не удалена: ${reason}`;

// --- Удаление ------------------------------------------------------------------------------

export interface RemoveOrphanArgs {
    mediaId: string;
    /** Папка, 1-based. */
    folder: number;
    /** Имя реплики на диске (original_id). */
    id: string;
    /** Язык перевода пары (провод: `es-ES`). */
    lang: MetadataToSubtitlesLanguages;
    /** Язык оригинала пары (провод: `ru-RU`): без него сервер искал бы реплику не в той паре. */
    languageFrom: MetadataToSubtitlesLanguages;
}

/**
 * Удалить сироту на сервере: json перевода (`/delete_translated_subtitle` — без него сборка
 * озвучку не возьмёт), затем её wav (`/delete_synt_audio`) — та же пара запросов, что убирает
 * перевод разбитой реплики. null — удалено; иначе — причина словами сервера. Отказ с переводом
 * звук не трогает: удалять дальше то, что сервер не отдал, значит оставить половину.
 */
export async function removeOrphanVoicing({
    mediaId,
    folder,
    id,
    lang,
    languageFrom,
}: RemoveOrphanArgs): Promise<string | null> {
    const body = { mediaId, id, folder_num: folder, lang, language_from: languageFrom };
    const translation: any = await deleteTranslatedSubtitleAction(body);
    if (translation && typeof translation.error === "string") {
        return translation.error;
    }
    // Сервер отвечает «ok», даже не найдя файла: пустой список перенесённого — не успех.
    const movedNothing =
        Array.isArray(translation?.moved_to_delete) && translation.moved_to_delete.length === 0;
    const audio: any = await deleteAudioAction({ ...body });
    if (audio && typeof audio.error === "string") {
        return movedNothing
            ? `сервер не нашёл файл перевода, а звук не убран: ${audio.error}`
            : `перевод убран, а звук — нет: ${audio.error}`;
    }
    return movedNothing ? "сервер не нашёл файл перевода" : null;
}
